import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { registerModalBackHandler } from '../../lib/native';
import { notifyInvoiceEvent } from '../../lib/invoiceNotify';
import {
  formatMoney,
  invoiceAmounts,
  invoiceFilePath,
  invoiceFormErrors,
  type InvoiceForm,
} from '../../lib/hostInvoice';
import { Button, Input } from '../design-system';

export interface InvoiceTrip {
  paymentId: string;
  matchId: string;
  title: string;
  dates: string;
  feeCents: number;
}

interface HostInvoiceModalProps {
  hostId: string;
  trips: InvoiceTrip[];
  currency: string;
  onClose: () => void;
  /** Called after the invoice was sent, so the board can refresh. */
  onSubmitted: () => void;
}

interface CompanyDetails {
  company_legal_name: string | null;
  company_nif: string | null;
  company_address: string | null;
}

function todayISO(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function CopyLine({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-start justify-between gap-3 text-xs">
      <div className="min-w-0">
        <span className="text-app-muted">{label}: </span>
        <span className="text-app-fg break-words">{value}</span>
      </div>
      <button
        type="button"
        className="shrink-0 text-gold underline underline-offset-2 sk-hit"
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }).catch(() => { /* clipboard unavailable: the text is still on screen */ });
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/**
 * The host sends Sekretly one invoice (a recibo verde) for the trips they chose.
 * They issue the recibo themselves on the Portal das Finanças, then enter its
 * details here and attach the file. The total is fixed: it is the sum of the
 * trips' fees, and the server refuses anything that doesn't match.
 */
export function HostInvoiceModal({ hostId, trips, currency, onClose, onSubmitted }: HostInvoiceModalProps) {
  const totalCents = useMemo(() => trips.reduce((n, t) => n + t.feeCents, 0), [trips]);
  const [company, setCompany] = useState<CompanyDetails | null>(null);
  const [form, setForm] = useState<Omit<InvoiceForm, 'file'>>({
    kind: 'recibo_verde',
    number: '',
    date: todayISO(),
    vatMode: 'exempt',
    vat: '',
    exemption: '',
    withholding: '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => registerModalBackHandler(() => { if (!sending) onClose(); }), [onClose, sending]);

  useEffect(() => {
    void supabase
      .from('platform_settings')
      .select('company_legal_name, company_nif, company_address')
      .limit(1)
      .maybeSingle()
      .then(({ data }) => { if (data) setCompany(data as CompanyDetails); });
  }, []);

  const fullForm: InvoiceForm = { ...form, file: file ? { type: file.type, size: file.size } : null };
  const errors = invoiceFormErrors(fullForm, totalCents, todayISO());
  const amounts = invoiceAmounts(totalCents, form);
  const err = (key: keyof typeof errors) => (showErrors ? errors[key] : undefined);
  const set = <K extends keyof typeof form>(key: K) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value as (typeof form)[K] }));
  const hasCompany = !!(company?.company_legal_name && company.company_nif);
  const description = `Trip curation services provided through Sekretly: ${trips.map((t) => t.title).join('; ')}`;

  async function send() {
    setShowErrors(true);
    if (Object.keys(errors).length > 0 || !file || !amounts) return;
    setSending(true);
    setSendError(null);

    const path = invoiceFilePath(hostId, file.name, crypto.randomUUID());
    const upload = await supabase.storage.from('host-invoices').upload(path, file, { contentType: file.type, upsert: false });
    if (upload.error) {
      console.error('invoice upload failed:', upload.error);
      setSendError("We couldn't upload your file. Check your connection and try again.");
      setSending(false);
      return;
    }

    const { data: invoiceId, error } = await supabase.rpc('submit_host_invoice', {
      p_payment_ids: trips.map((t) => t.paymentId),
      p_kind: form.kind,
      p_number: form.number.trim(),
      p_date: form.date,
      p_vat_cents: amounts.vatCents,
      p_vat_exemption: form.vatMode === 'exempt' ? form.exemption.trim() : null,
      p_withholding_cents: amounts.withholdingCents,
      p_file_path: path,
    });
    setSending(false);
    if (error) {
      console.error('submit_host_invoice failed:', error);
      // The server's messages are written for the host (see the function).
      setSendError(error.message || "We couldn't send your invoice. Please try again.");
      return;
    }
    if (typeof invoiceId === 'string') void notifyInvoiceEvent(invoiceId, 'submitted');
    onSubmitted();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60" onClick={() => { if (!sending) onClose(); }}>
      <div
        role="dialog"
        aria-label="Send your invoice"
        className="bg-app-bg md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-xl max-h-dvh-92 overflow-y-auto pb-safe-or-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 md:p-6 flex flex-col gap-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-app-fg">Send your invoice</h2>
              <p className="text-xs text-app-muted mt-0.5">
                {trips.length} trip{trips.length === 1 ? '' : 's'} · {formatMoney(totalCents, currency)}
              </p>
            </div>
            <button type="button" aria-label="Close" onClick={onClose} disabled={sending} className="text-app-muted text-xl leading-none sk-hit">×</button>
          </div>

          <ul className="flex flex-col gap-1.5 text-sm">
            {trips.map((t) => (
              <li key={t.paymentId} className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-app-fg">{t.title}<span className="text-app-muted"> · {t.dates}</span></span>
                <span className="shrink-0 text-app-fg">{formatMoney(t.feeCents, currency)}</span>
              </li>
            ))}
          </ul>

          <section className="rounded-2xl border border-app-border bg-app-card p-4 flex flex-col gap-2.5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-app-muted">1 · Issue your recibo</h3>
            <p className="text-xs text-app-muted leading-relaxed">
              Issue it on the Portal das Finanças, made out to Sekretly for exactly {formatMoney(totalCents, currency)}
              {' '}(VAT included, if you charge it).
            </p>
            {hasCompany ? (
              <>
                <CopyLine label="Name" value={company!.company_legal_name!} />
                <CopyLine label="NIF" value={company!.company_nif!} />
                {company!.company_address ? <CopyLine label="Address" value={company!.company_address} /> : null}
              </>
            ) : (
              <p className="text-xs text-gold">Sekretly’s invoicing details aren’t available yet — contact us and we’ll send them.</p>
            )}
            <CopyLine label="Description" value={description} />
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-app-muted">2 · Tell us what it says</h3>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="inv-kind" className="text-footnote font-medium text-app-fg-2">Type</label>
                <select id="inv-kind" value={form.kind} onChange={set('kind')} disabled={sending}
                  className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2.5 text-base text-app-fg">
                  <option value="recibo_verde">Recibo verde</option>
                  <option value="fatura">Fatura</option>
                </select>
              </div>
              <Input label="Invoice number" value={form.number} onChange={set('number')} error={err('number')} disabled={sending} />
            </div>
            <Input label="Invoice date" type="date" value={form.date} onChange={set('date')} error={err('date')} disabled={sending} />

            <fieldset className="flex flex-col gap-2" disabled={sending}>
              <legend className="text-footnote font-medium text-app-fg-2 mb-1">VAT on your invoice</legend>
              <label className="flex items-center gap-2 text-sm text-app-fg sk-tall">
                <input type="radio" name="vat-mode" checked={form.vatMode === 'exempt'} onChange={() => setForm((f) => ({ ...f, vatMode: 'exempt' }))} />
                No VAT (I’m exempt)
              </label>
              <label className="flex items-center gap-2 text-sm text-app-fg sk-tall">
                <input type="radio" name="vat-mode" checked={form.vatMode === 'vat'} onChange={() => setForm((f) => ({ ...f, vatMode: 'vat' }))} />
                My invoice charges VAT
              </label>
            </fieldset>
            {form.vatMode === 'exempt' ? (
              <Input label="Why no VAT?" placeholder="Isento art. 53.º CIVA" value={form.exemption} onChange={set('exemption')} error={err('exemption')} disabled={sending} />
            ) : (
              <Input label="VAT amount shown on the invoice" inputMode="decimal" placeholder="0,00" value={form.vat} onChange={set('vat')} error={err('vat')} disabled={sending} />
            )}
            <Input label="Tax withheld at source (if any)" inputMode="decimal" placeholder="0,00" value={form.withholding} onChange={set('withholding')} error={err('withholding')} disabled={sending} />

            <div className="flex flex-col gap-1.5">
              <label htmlFor="inv-file" className="text-footnote font-medium text-app-fg-2">Your recibo (PDF, JPG or PNG, up to 5 MB)</label>
              <input id="inv-file" type="file" accept="application/pdf,image/jpeg,image/png" disabled={sending}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm text-app-fg" />
              {err('file') ? <p className="text-caption text-crimson">{err('file')}</p> : null}
            </div>
          </section>

          {amounts ? (
            <div className="rounded-2xl bg-gold/8 border border-gold/25 p-4 flex flex-col gap-1 text-sm" aria-label="Summary">
              <div className="flex justify-between"><span className="text-app-muted">Invoice total</span><span className="text-app-fg font-medium">{formatMoney(amounts.totalCents, currency)}</span></div>
              <div className="flex justify-between"><span className="text-app-muted">of which base amount</span><span className="text-app-fg">{formatMoney(amounts.baseCents, currency)}</span></div>
              <div className="flex justify-between"><span className="text-app-muted">of which VAT</span><span className="text-app-fg">{formatMoney(amounts.vatCents, currency)}</span></div>
              {amounts.withholdingCents > 0 ? (
                <div className="flex justify-between"><span className="text-app-muted">Tax withheld</span><span className="text-app-fg">−{formatMoney(amounts.withholdingCents, currency)}</span></div>
              ) : null}
              <div className="flex justify-between pt-1 border-t border-gold/20"><span className="text-app-muted">We pay you</span><span className="text-emerald-500 font-semibold">{formatMoney(amounts.payableCents, currency)}</span></div>
            </div>
          ) : null}

          {sendError ? <p role="alert" className="text-sm text-crimson">{sendError}</p> : null}

          <div className="flex gap-2">
            <Button onClick={() => void send()} disabled={sending}>{sending ? 'Sending…' : 'Send invoice'}</Button>
            <Button variant="secondary" onClick={onClose} disabled={sending}>Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HostInvoiceModal;
